import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/svg/logo.svg';
import './nav.style.scss';
import { FaCaretDown } from 'react-icons/fa';
import { FiBell, FiLogOut } from 'react-icons/fi';
import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../Context/Notification.context';
import { UserLoginDetail } from './UserInterface';
import { ucFirst } from '../..//utils/Helper';
import { markNotificationRead } from '../../api/notification';
import {
  getNotificationContextLine,
  getNotificationMetaLine,
} from '../../utils/notificationDisplay';

type INavProps = {
  isLoggedIn: boolean;
  user: UserLoginDetail | null;
};

const Nav = ({ isLoggedIn, user }: INavProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { notifications, setNotifications, getConnection } = useNotification();

  const [isProfileDropdownVisible, setIsProfileDropdownVisible] =
    useState(false);
  const [isNotificationDropdownVisible, setIsNotificationDropdownVisible] =
    useState(false);
  const [prevNotificationCount, setPrevNotificationCount] = useState(0);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationAutoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    if (notifications.length > prevNotificationCount && notifications.length > 0) {
      setIsNotificationDropdownVisible(true);

      if (notificationAutoCloseRef.current) {
        clearTimeout(notificationAutoCloseRef.current);
      }

      notificationAutoCloseRef.current = setTimeout(() => {
        setIsNotificationDropdownVisible(false);
        notificationAutoCloseRef.current = null;
      }, 5000);
    }

    setPrevNotificationCount(notifications.length);
  }, [notifications, prevNotificationCount]);

  useEffect(() => {
    return () => {
      if (notificationAutoCloseRef.current) {
        clearTimeout(notificationAutoCloseRef.current);
      }
    };
  }, []);

  useEffect(() => {
    getConnection();
  }, []);

  const menuItem = [
    {
      name: 'Dashboard',
      path: '/',
    },
    {
      name: 'Teachers',
      path: '/teachers',
    },
    {
      name: 'Subjects',
      path: '/subjects',
    },
    {
      name: 'Questions',
      path: '/questions',
    },
    {
      name: 'Templates',
      path: '/templates',
    },
    {
      name: 'Exams',
      path: '/exams',
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("username")
    localStorage.removeItem("email")
    localStorage.removeItem("userId")
    window.location.pathname = "/login"
  };

  const toggleNotificationDropdown = () => {
    if (notificationAutoCloseRef.current) {
      clearTimeout(notificationAutoCloseRef.current);
      notificationAutoCloseRef.current = null;
    }

    setIsNotificationDropdownVisible((isOpen) => !isOpen);
  };

  const handleMarkNotificationAsRead = (id: string) => {
    markNotificationRead(id).then(res => {
      const newNoti = notifications.map(item => {
        if (item.id === id) {
          return {
            ...item,
            is_read: true,
          };
        }
        return item;
      });

      setNotifications(newNoti);
    });
  };

  const adminOnlyNav = menuItem.filter(item => {
    return !(
      (item.path === '/teachers' && user?.role !== 'admin') ||
      (item.path === '/subjects' && user?.role !== 'admin')
    );
  });

  // const adminOnlyNav = menuItem.filter((item) => {
  //   return !(item.path === "/teachers" && user?.role !== "admin");
  // });

  const profilePic =
    user && user.profile_photo !== null
      ? user.profile_photo
      : 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg';

  const roleName = user && user.role ? ucFirst(user.role) : null;

  return (
    <nav className="teacher-nav">
      <div className="logo">
        <img src={logo} alt="AI Exam Creator" tabIndex={0} />
      </div>

      {isLoggedIn && (
        <>
          <div className="menu">
            {adminOnlyNav.map((item, index) => {
              const isDisabled =
                item.path === '/teachers' && user?.role !== 'admin';
              const isActive = pathname === item.path;
              return (
                <div
                  key={item?.name}
                  tabIndex={index + 1}
                  // onClick={() => !isDisabled && handleNavigate(item.path)}
                  // className={`menu_item ${isActive && "active"} ${isDisabled ? "disabled" : ""}`}
                  title={isDisabled ? 'access denied' : item?.name}
                  onClick={() => handleNavigate(item.path)}
                  className={`menu_item ${pathname === item.path && 'active'}`}
                >
                  <p>{item?.name}</p>
                </div>
              );
            })}
          </div>

          <div className="profile">
            {notifications.length > 0 && (
              <div
                className="notification_wrapper"
                ref={notificationRef}
                onBlur={(event) => {
                  if (
                    !event.currentTarget.contains(
                      event.relatedTarget as Node | null
                    )
                  ) {
                    setTimeout(
                      () => setIsNotificationDropdownVisible(false),
                      150
                    );
                  }
                }}
              >
                <div
                  tabIndex={0}
                  className="notification_icon"
                  role="button"
                  aria-label="Notifications"
                  aria-expanded={isNotificationDropdownVisible}
                  onClick={toggleNotificationDropdown}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleNotificationDropdown();
                    }
                  }}
                >
                  <FiBell size={22} color="#fff" />

                  <div className="dot">{notifications.length}</div>
                </div>

                {isNotificationDropdownVisible && (
                  <div className="notification_dropdown">
                    <h3>Notifications </h3>

                    {notifications.map(item => {
                      return (
                        <div
                          className={`notification ${!item.is_read ? 'unread' : ''}`}
                          key={item.id || item.bulk_session_id || Math.random().toString()}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            const destination = item.bulk_session_id
                              ? `/questions/preview-question/${item.bulk_session_id}`
                              : item.generation_job_id
                                ? `/questions?generation_job_id=${item.generation_job_id}`
                                : '/questions';
                            if (item.id) handleMarkNotificationAsRead(item.id);
                            setIsNotificationDropdownVisible(false);
                            handleNavigate(destination);
                          }}
                        >
                          <h4>{item.title}</h4>
                          <p className="notification__context">
                            {getNotificationContextLine(item)}
                          </p>
                          <p className="notification__meta">
                            {getNotificationMetaLine(item)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="profile_image">
              <img src={profilePic} alt="user profile" />
            </div>

            <div
              className="profile_bio"
              tabIndex={9}
              onFocus={() => setIsProfileDropdownVisible(true)}
              onBlur={() =>
                setTimeout(() => setIsProfileDropdownVisible(false), 1500)
              }
            >
              <p className="name">
                {localStorage.getItem('username') || 'David Dolan'}{' '}
                <FaCaretDown />
              </p>
              <p className="role">{roleName}</p>
            </div>

            {isProfileDropdownVisible && (
              <div className="profile_dropdown">
                <h4 onClick={handleLogout}>
                  <span>
                    <FiLogOut />
                  </span>
                  Logout
                </h4>
              </div>
            )}
          </div>
        </>
      )}
    </nav>
  );
};

export default Nav;
